import React, { useEffect, useState } from 'react';
import Card from '../../components/common/Card';
import Header from '../../components/common/Header';
import { api } from '../../services/api';
import type { SecurityStats } from '../../types';
import { useTranslation } from 'react-i18next';

import { devError } from '@/utils/devLog';
const SecurityDashboard: React.FC = () => {
  const { t } = useTranslation();
  const [stats, setStats] = useState<SecurityStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchStats = async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await api.getAdminSecurityStats();
        setStats(data);
      } catch (err: any) {
        devError('Failed to load security stats', err);
        setError(err.message || t('common.error'));
      } finally {
        setLoading(false);
      }
    };
    fetchStats();
  }, [t]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (error || !stats) {
    return (
      <div className="p-4 bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 rounded-lg">
        {error || t('common.error')}
      </div>
    );
  }

  const formatPercent = (v: number) => `${v.toFixed(1)}%`;

  return (
    <div className="space-y-6">
      <Header title={t('SecurityDashboard.header.title')} />

      {/* Bloc IP & OTP */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card className="p-5">
          <h3 className="text-sm font-semibold text-slate-600 dark:text-slate-300 mb-2">
            {t('SecurityDashboard.ipBlocked.title')}
          </h3>
          <p className="text-3xl font-bold text-slate-900 dark:text-white">
            {stats.blockedIpsActive}
          </p>
          <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
            {t('SecurityDashboard.ipBlocked.last24h')}{' '}
            : <span className="font-semibold">{stats.blockedIpsLast24h}</span>
          </p>
        </Card>

        <Card className="p-5">
          <h3 className="text-sm font-semibold text-slate-600 dark:text-slate-300 mb-2">
            {t('SecurityDashboard.ipActivity.title')}
          </h3>
          <p className="text-3xl font-bold text-slate-900 dark:text-white">
            {stats.ipWithActivity24h}
          </p>
          <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
            {t('SecurityDashboard.ipActivity.hint')}
          </p>
        </Card>

        <Card className="p-5">
          <h3 className="text-sm font-semibold text-slate-600 dark:text-slate-300 mb-2">
            {t('SecurityDashboard.otp.title')}
          </h3>
          <p className="text-2xl font-bold text-slate-900 dark:text-white">
            {stats.otpSentLast24h}{' '}
            <span className="text-sm font-normal text-slate-500 dark:text-slate-400">
              {t('SecurityDashboard.otp.sent')}
            </span>
          </p>
          <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
            {t('SecurityDashboard.otp.validationRate')}:{' '}
            <span className="font-semibold">
              {formatPercent(stats.otpSuccessRate24h || 0)}
            </span>
          </p>
        </Card>
      </div>

      {/* Bloc Tickets & Avis */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card className="p-5">
          <h3 className="text-sm font-semibold text-slate-600 dark:text-slate-300 mb-2">
            {t('SecurityDashboard.tickets.openTickets')}
          </h3>
          <p className="text-3xl font-bold text-slate-900 dark:text-white">
            {stats.openTickets}
          </p>
          <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
            {t('SecurityDashboard.tickets.firstResponse')}{' '}
            :{' '}
            <span className="font-semibold">
              {stats.avgFirstResponseHours !== null
                ? `${stats.avgFirstResponseHours.toFixed(1)} h`
                : t('SecurityDashboard.notAvailable')}
            </span>
          </p>
        </Card>

        <Card className="p-5">
          <h3 className="text-sm font-semibold text-slate-600 dark:text-slate-300 mb-2">
            {t('SecurityDashboard.reviews.title')}
          </h3>
          <p className="text-3xl font-bold text-slate-900 dark:text-white">
            {stats.reviewsCount30d}
          </p>
          <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
            {t('SecurityDashboard.reviews.avgRating')}{' '}
            :{' '}
            <span className="font-semibold">
              {stats.avgRating !== null ? stats.avgRating.toFixed(1) : t('SecurityDashboard.notAvailable')} / 5
            </span>
          </p>
        </Card>

        <Card className="p-5">
          <h3 className="text-sm font-semibold text-slate-600 dark:text-slate-300 mb-2">
            {t('SecurityDashboard.twoFa.title')}
          </h3>
          <p className="text-3xl font-bold text-slate-900 dark:text-white">
            {stats.twoFaEnabledUsers}
          </p>
          <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
            {t('SecurityDashboard.twoFa.hint')}
          </p>
        </Card>
      </div>

      {/* Bloc Comptes expirés */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card className="p-5">
          <h3 className="text-sm font-semibold text-slate-600 dark:text-slate-300 mb-2">
            {t('SecurityDashboard.expired.expired24h')}
          </h3>
          <p className="text-3xl font-bold text-slate-900 dark:text-white">
            {stats.expiredAccounts24h}
          </p>
          <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
            {t('SecurityDashboard.expired.hint')}
          </p>
        </Card>

        <Card className="p-5">
          <h3 className="text-sm font-semibold text-slate-600 dark:text-slate-300 mb-2">
            {t('SecurityDashboard.expired.total')}
          </h3>
          <p className="text-3xl font-bold text-slate-900 dark:text-white">
            {stats.expiredAccountsTotal}
          </p>
          <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
            {t('SecurityDashboard.expired.totalHint')}
          </p>
        </Card>
      </div>
    </div>
  );
};

export default SecurityDashboard;

